import React from "react";
import { ArrowRight, Clock, TrendingUp, Users, Lightbulb } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const servicesData = [{
  title: "Lifelong Learning Growth Strategy",
  category: "Strategic Growth",
  description: "We co-design sustainable growth models that align with your institution's mission. Move beyond short-term tactics to build a robust, lifelong learning ecosystem.",
  duration: "6–12 Month Engagement",
  pricing: "Custom Scope",
  icon: TrendingUp
}, {
  title: "Learner Experience & Retention Design",
  category: "Experience Design",
  description: "Create learner-centered pathways that drive engagement and retention. We help you map and optimize the entire student journey for adult learners.",
  duration: "3–6 Month Engagement",
  pricing: "Project Based",
  icon: Users
}, {
  title: "Leadership & Organizational Capability",
  category: "Capacity Building",
  description: "Build the internal muscle to sustain growth. We embed within your team to develop the leadership and operational structures needed for long-term success.",
  duration: "Ongoing Partnership",
  pricing: "Retainer Model",
  icon: Lightbulb
}];
export default function OurFocusAreas() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="our-focus-areas" ref={ref} className="bg-background py-24 sm:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] bg-secondary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[10%] -left-[10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className={`max-w-2xl mx-auto text-center mb-16 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary mb-6">
            Our Focus Areas
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            We don't just deliver strategies; we build the internal capability
            to execute them. Our work focuses on three critical pillars of
            institutional growth.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {servicesData.map((service, index) => <div key={index} data-index={index} className={`transition-all duration-700 delay-${index * 150} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
              <Card className="h-full flex flex-col border-border/60 bg-card/50 backdrop-blur-sm hover:shadow-lg hover:border-secondary/30 transition-all duration-300 group">
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start mb-4">
                    <Badge variant="secondary" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 px-3 py-1 text-xs font-medium tracking-wide uppercase rounded-sm">
                      {service.category}
                    </Badge>
                    <service.icon className="w-6 h-6 text-primary/40 group-hover:text-secondary transition-colors duration-300" />
                  </div>
                  <CardTitle className="font-serif text-2xl text-primary leading-tight min-h-[3.5rem]">
                    {service.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex-grow">
                  <p className="text-muted-foreground leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>

                <CardFooter className="flex flex-col items-start pt-6 border-t border-border/40 mt-auto">
                  <div className="w-full flex justify-between items-center mb-6 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      {service.duration}
                    </span>
                  </div>

                  <Link to="/ServicesPage" className="group/link inline-flex items-center text-primary font-semibold hover:text-secondary transition-colors">
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover/link:translate-x-1.5" />
                  </Link>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}